import React, {useEffect, useRef, useState} from "react";
import {useForm as rectUseForm} from "react-hook-form";
import {zodResolver} from '@hookform/resolvers/zod';
import axios from "axios";
import {z} from "zod";

import {useModal} from "@/hooks/useModal.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import ErrorNotification from "@/components/dialogs/ErrorNotification.jsx";

export const useForm = ({schema = z.object({}), endpoint, defaultValues}) => {
    const {
        // register,
        register: originalRegister,
        handleSubmit,
        watch,
        clearErrors,
        setValue,
        setError,
        formState,
        reset,
        getValues,
        getFieldState,
    } = rectUseForm({
        resolver: zodResolver(schema),
        defaultValues: defaultValues ?? {},
    });

    const [isValid, setIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isLoadingRef = useRef(false);

    const {openModal} = useModal();
    const {storeData, getStoredData} = useLocalStorage() || {
        storeData: () => {
        },
        getStoredData: () => {
        },
    };

    const fields = watch();

    useEffect(() => {
        setIsLoading(isLoadingRef.current);
    }, [isLoadingRef.current]);

    useEffect(() => {
        const validateSchema = async () => {
            const result = await schema.safeParseAsync(fields);
            setIsValid(result.success);

            // if (!result.success) {
            //     console.log('validateSchema error', result.error);
            // }
        };

        validateSchema();
    }, [fields]);

    // Fill data from storage
    useEffect(() => {
        const storedData = getStoredData();
        if (storedData) {
            for (const [key, value] of Object.entries(storedData)) {
                setValue(key, value);
            }
        }
    }, [getStoredData]);

    // Custom register function to track user interaction
    const register = (name, options) => {
        const {onChange, ...registerOptions} = originalRegister(name, options);

        return {
            ...registerOptions,
            onChange: (e) => {
                // Call the original onChange to update the form state
                onChange(e).then(r => {
                    storeData(name, e.target.value);
                });
            },
        };
    };

    const setCustomError = (field, message) => {
        setError(field, {type: 'custom', message: message});
    }

    const onSubmit = (handler, resetOnSuccess) => {
        return (event) => {
            if (!isValid || isLoadingRef.current) {
                return false;
            }

            submit(handler, resetOnSuccess)(event);
        };
    }


    const submit = (handler, resetOnSuccess = true) => {
        return handleSubmit((data) => {
            isLoadingRef.current = true;

            callEndpoint(data, resetOnSuccess, (endpointResponse) => {
                if (typeof handler == 'function') {
                    handler(endpointResponse, data);
                }
            }).then(() => {
                isLoadingRef.current = false;
            });
        });
    };

    const callEndpoint = async (data, resetOnSuccess, callback) => {
        try {
            const response = await axios.post(endpoint, data, {
                withCredentials: true,
                validateStatus: function (status) {
                    return status < 500;
                },
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'multipart/form-data',
                },
            });

            switch (response.status) {
                case 200:
                    callback(response);
                    if (resetOnSuccess) {
                        reset();
                    }

                    return {success: true};
                    break;
                case 422:
                    for (const field in fields) {
                        if (response.data?.errors?.[field]) {
                            setCustomError(field, response.data.errors[field][0]);
                            // break;
                        }
                    }

                    return {success: false, errors: response.data.errors};
                    break;
                default:
                    openModal(
                        <ErrorNotification>
                            {response?.data?.errorNotification}
                        </ErrorNotification>
                    );

                    return {success: false};
            }
        } catch (error) {
            console.error(error);

            openModal(
                <ErrorNotification/>
            );

            return {success: false};
        }
    };

    return {
        register,
        onSubmit,
        callEndpoint,
        getValues,
        getFieldState,
        setValue,
        state: formState,
        clearErrors,
        setError: setCustomError,
        watch,
        reset,
        isValid,
        isLoading,
    };
};
